import { useState, type JSX } from "react";
import { opsArticles, opsCategories, type OpsArticle } from "@/mocks/opsKnowledge";

interface EditDraft {
  title: string;
  category: string;
  tags: string;
  relatedDevices: string;
  content: string;
}

/* -------- Markdown Renderer -------- */

function highlightCode(code: string): React.ReactNode[] {
  const lines = code.split("\n");
  return lines.map((line, li) => {
    const highlighted: React.ReactNode[] = [];
    const pattern = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)|(\b\d+\.?\d*\b)|(\b(?:function|const|let|var|if|else|return|import|export|from|class|extends|new|this|async|await|try|catch|throw|for|while|switch|case|break|continue|default|typeof|instanceof|interface|type|enum|true|false|null|undefined|yield|static|private|public|protected|readonly|abstract|implements|extends|super|module|require|export\s+default|void|never|unknown|any|boolean|number|string|symbol|object|keyof|in|of)\b)|(<\/?[a-zA-Z][^>]*\/?>)/g;
    let lastIdx = 0;
    let m: RegExpExecArray | null;
    const localPattern = new RegExp(pattern.source, "g");
    while ((m = localPattern.exec(line)) !== null) {
      if (m.index > lastIdx) {
        highlighted.push(<span key={`${li}-${lastIdx}`}>{line.slice(lastIdx, m.index)}</span>);
      }
      if (m[1]) highlighted.push(<span key={`${li}-${m.index}`} className="code-comment">{m[1]}</span>);
      else if (m[2]) highlighted.push(<span key={`${li}-${m.index}`} className="code-string">{m[2]}</span>);
      else if (m[3]) highlighted.push(<span key={`${li}-${m.index}`} className="code-number">{m[3]}</span>);
      else if (m[4]) highlighted.push(<span key={`${li}-${m.index}`} className="code-keyword">{m[4]}</span>);
      else if (m[5]) highlighted.push(<span key={`${li}-${m.index}`} className="code-func">{m[5]}</span>);
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < line.length) {
      highlighted.push(<span key={`${li}-${lastIdx}`}>{line.slice(lastIdx)}</span>);
    }
    return <div key={li}>{highlighted.length > 0 ? highlighted : line}</div>;
  });
}

function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;
  const regex = /(`[^`]+`)|(\*\*[^*]+\*\*)|(!\[([^\]]*)\]\(([^)]+)\))|(\[([^\]]+)\]\(([^)]+)\))/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    if (m[1]) parts.push(<code key={key++} className="text-xs bg-gray-100 text-rose-600 px-1.5 py-0.5 rounded font-mono">{m[1].slice(1, -1)}</code>);
    else if (m[2]) parts.push(<strong key={key++} className="font-semibold text-gray-900">{m[2].slice(2, -2)}</strong>);
    else if (m[3]) parts.push(<img key={key++} src={m[5]} alt={m[4]} className="inline-block max-w-full my-2 rounded-lg" />);
    else if (m[6]) parts.push(<a key={key++} href={m[8]} target="_blank" rel="nofollow" className="text-teal-600 hover:text-teal-700 underline">{m[7]}</a>);
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  if (parts.length === 0) return text;
  if (parts.length === 1 && typeof parts[0] === "string") return parts[0];
  return <>{parts}</>;
}

function renderCodeBlock(code: string, language: string, key: number): JSX.Element {
  return (
    <div key={key} className="my-4 rounded-lg overflow-hidden border border-gray-200">
      <div className="code-block-header">
        <span className="text-xs text-gray-400 font-mono">{language || "code"}</span>
        <span className="text-xs text-gray-500"><i className="ri-file-copy-line"></i></span>
      </div>
      <pre className="code-block-body">
        <code>{highlightCode(code)}</code>
      </pre>
    </div>
  );
}

function renderTable(tableLines: string[], key: number): JSX.Element {
  const headers = tableLines[0].split("|").filter(Boolean).map((h) => h.trim());
  const rows = tableLines.slice(2).map((r) => r.split("|").filter(Boolean).map((c) => c.trim()));
  return (
    <div key={key} className="mt-3 mb-4 overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50">
            {headers.map((h) => (
              <th key={h} className="border border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-700">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-gray-50/50">
              {row.map((cell, ci) => (
                <td key={ci} className="border border-gray-200 px-3 py-2 text-xs text-gray-600">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderMarkdown(content: string): JSX.Element[] {
  const elements: JSX.Element[] = [];
  let key = 0;
  const codeRegex = /```(\w*)\r?\n([\s\S]*?)```/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;

  while ((m = codeRegex.exec(content)) !== null) {
    if (m.index > lastIdx) {
      elements.push(...renderTextSegment(content.slice(lastIdx, m.index), key));
      key += 1000;
    }
    elements.push(renderCodeBlock(m[2].trimEnd(), m[1], key++));
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < content.length) {
    elements.push(...renderTextSegment(content.slice(lastIdx), key));
  }
  return elements;
}

function renderTextSegment(text: string, baseKey: number): JSX.Element[] {
  const lines = text.split("\n");
  const result: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") {
      result.push(<div key={baseKey + i} className="h-2"></div>);
      i++;
      continue;
    }

    // Table detection
    if (line.trim().startsWith("|")) {
      const tbl: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        tbl.push(lines[i].trim());
        i++;
      }
      if (tbl.length >= 2) {
        result.push(renderTable(tbl, baseKey + i));
      }
      continue;
    }

    // Headers
    if (line.startsWith("## ")) {
      result.push(<h3 key={baseKey + i} className="text-base font-bold text-gray-900 mt-6 mb-2">{renderInline(line.replace("## ", ""))}</h3>);
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      result.push(<h4 key={baseKey + i} className="text-sm font-semibold text-gray-800 mt-4 mb-1">{renderInline(line.replace("### ", ""))}</h4>);
      i++;
      continue;
    }
    if (line.startsWith("#### ")) {
      result.push(<h5 key={baseKey + i} className="text-sm font-medium text-gray-700 mt-3 mb-1">{renderInline(line.replace("#### ", ""))}</h5>);
      i++;
      continue;
    }

    // Ordered / unordered lists
    if (line.match(/^\d+\.\s/) || line.trim().startsWith("- ")) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const l = lines[i].trim();
        if (l.match(/^\d+\.\s/) || l.startsWith("- ")) {
          listItems.push(l.replace(/^\d+\.\s|- /, ""));
          i++;
        } else if (l === "") {
          i++;
          continue;
        } else {
          break;
        }
      }
      result.push(
        <ul key={baseKey + i} className="list-disc ml-5 my-2 space-y-1">
          {listItems.map((item, idx) => (
            <li key={idx} className="text-sm text-gray-700">{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Standalone image
    const imgMatch = line.trim().match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      result.push(
        <figure key={baseKey + i} className="my-4">
          <img src={imgMatch[2]} alt={imgMatch[1] || ""} className="rounded-lg max-w-full" />
          {imgMatch[1] && <figcaption className="text-xs text-gray-400 mt-1.5 text-center">{imgMatch[1]}</figcaption>}
        </figure>
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (line.trim() === "---" || line.trim() === "***") {
      result.push(<hr key={baseKey + i} className="my-4 border-gray-200" />);
      i++;
      continue;
    }

    // Regular paragraph with inline parsing
    result.push(<p key={baseKey + i} className="text-sm text-gray-700 leading-relaxed mb-3">{renderInline(line)}</p>);
    i++;
  }

  return result;
}

/* -------- Component -------- */

function KnowledgePage() {
  const [articles, setArticles] = useState<OpsArticle[]>(opsArticles);
  const [activeCategory, setActiveCategory] = useState("全部");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedArticle, setSelectedArticle] = useState<OpsArticle | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft>({
    title: "",
    category: "",
    tags: "",
    relatedDevices: "",
    content: "",
  });

  const filteredArticles = articles.filter((a) => {
    const matchCategory = activeCategory === "全部" || a.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchCategory && matchSearch;
  });

  const openEditMode = (article: OpsArticle) => {
    setEditDraft({
      title: article.title,
      category: article.category,
      tags: article.tags.join("、"),
      relatedDevices: article.relatedDevices.join("、"),
      content: article.content,
    });
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (!selectedArticle) return;
    const updated: OpsArticle = {
      ...selectedArticle,
      title: editDraft.title,
      category: editDraft.category,
      tags: editDraft.tags.split(/[,，、]/).map((t) => t.trim()).filter(Boolean),
      relatedDevices: editDraft.relatedDevices.split(/[,，、]/).map((t) => t.trim()).filter(Boolean),
      content: editDraft.content,
      updatedAt: new Date().toISOString().slice(0, 10),
    };
    setArticles((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    setSelectedArticle(updated);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleDeleteArticle = () => {
    if (!selectedArticle) return;
    setArticles((prev) => prev.filter((a) => a.id !== selectedArticle.id));
    setSelectedArticle(null);
    setIsEditing(false);
  };

  const handleNewArticle = () => {
    const newId = `kb-${String(articles.length + 1).padStart(3, "0")}`;
    const newArticle: OpsArticle = {
      id: newId,
      title: "新文章",
      category: "巡检规范",
      tags: [],
      author: "当前用户",
      createdAt: new Date().toISOString().slice(0, 10),
      updatedAt: new Date().toISOString().slice(0, 10),
      viewCount: 0,
      content: "",
      relatedDevices: [],
    };
    setArticles((prev) => [newArticle, ...prev]);
    setSelectedArticle(newArticle);
    openEditMode(newArticle);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">运维知识库</h1>
          <p className="text-sm text-gray-500 mt-1">设备运维操作指南、故障排查、应急预案</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="搜索知识库..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
            />
          </div>
          <button
            onClick={handleNewArticle}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            新建文章
          </button>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {opsCategories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-3.5 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
              activeCategory === cat
                ? "bg-teal-500 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredArticles.map((article) => (
          <button
            key={article.id}
            onClick={() => {
              setSelectedArticle(article);
              setIsEditing(false);
            }}
            className={`bg-white rounded-lg border p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${
              selectedArticle?.id === article.id
                ? "border-teal-300 ring-1 ring-teal-200"
                : "border-gray-200"
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <span className="text-xs font-medium px-2 py-0.5 bg-teal-50 text-teal-700 rounded whitespace-nowrap">
                {article.category}
              </span>
              <span className="text-xs text-gray-400">{article.updatedAt}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-1">{article.title}</h3>
            <div className="flex items-center gap-2 mb-3">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.viewCount.toLocaleString()}
              </span>
              {article.relatedDevices.length > 0 && (
                <span className="flex items-center gap-1">
                  <i className="ri-glasses-line"></i>
                  {article.relatedDevices.length} 设备
                </span>
              )}
            </div>
          </button>
        ))}

        {filteredArticles.length === 0 && (
          <div className="col-span-2 text-center py-16 text-gray-400">
            <i className="ri-book-read-line text-4xl block mb-3"></i>
            未找到匹配的知识文章
          </div>
        )}
      </div>

      {/* Article Detail Drawer */}
      {selectedArticle && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => {
              setSelectedArticle(null);
              setIsEditing(false);
            }}
          ></div>
          <div className="fixed top-0 right-0 h-full w-full md:w-[600px] bg-white z-50 shadow-2xl animate-[slideIn_0.3s_ease-out] overflow-y-auto">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              {isEditing ? (
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-edit-line text-teal-600 text-sm"></i>
                    <span className="text-sm font-semibold text-teal-700">编辑模式</span>
                  </div>
                  <input
                    type="text"
                    value={editDraft.title}
                    onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                    className="w-full text-lg font-bold text-gray-900 border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                    placeholder="文章标题"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium px-2 py-0.5 bg-teal-50 text-teal-700 rounded">
                    {selectedArticle.category}
                  </span>
                  <h2 className="text-lg font-bold text-gray-900">{selectedArticle.title}</h2>
                </div>
              )}
              <div className="flex items-center gap-2">
                {!isEditing && (
                  <button
                    onClick={() => openEditMode(selectedArticle)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-colors whitespace-nowrap"
                  >
                    <i className="ri-edit-line"></i>
                    编辑
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedArticle(null);
                    setIsEditing(false);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
            </div>

            {/* Edit Mode: Form Fields */}
            {isEditing ? (
              <>
                {/* Meta Row */}
                <div className="px-6 py-4 border-b border-gray-100 space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">分类</label>
                      <select
                        value={editDraft.category}
                        onChange={(e) => setEditDraft((d) => ({ ...d, category: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                      >
                        {opsCategories.filter((c) => c !== "全部").map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">作者</label>
                      <input
                        type="text"
                        value={selectedArticle.author}
                        disabled
                        className="w-full px-3 py-2 text-sm border border-gray-100 rounded-lg bg-gray-50 text-gray-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">标签（逗号分隔）</label>
                    <input
                      type="text"
                      value={editDraft.tags}
                      onChange={(e) => setEditDraft((d) => ({ ...d, tags: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                      placeholder="巡检、SOP、日常操作"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">关联设备（逗号分隔）</label>
                    <input
                      type="text"
                      value={editDraft.relatedDevices}
                      onChange={(e) => setEditDraft((d) => ({ ...d, relatedDevices: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100"
                      placeholder="#A001、#A005、#A012"
                    />
                  </div>
                </div>

                {/* Content Editor */}
                <div className="px-6 py-4">
                  <label className="block text-xs font-medium text-gray-500 mb-2">内容（Markdown）</label>
                  <textarea
                    value={editDraft.content}
                    onChange={(e) => setEditDraft((d) => ({ ...d, content: e.target.value }))}
                    className="w-full h-[420px] px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-100 resize-none font-mono leading-relaxed"
                    placeholder="在此编辑 Markdown 内容..."
                  ></textarea>
                </div>

                {/* Edit Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                  <button
                    onClick={handleDeleteArticle}
                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-red-500 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i>
                    删除文章
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="px-5 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-check-line mr-1.5"></i>
                      保存修改
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* View Mode Meta */}
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-6 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {selectedArticle.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    创建 {selectedArticle.createdAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-refresh-line"></i>
                    更新 {selectedArticle.updatedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {selectedArticle.viewCount.toLocaleString()} 次阅读
                  </span>
                </div>

                {/* Tags */}
                {selectedArticle.tags.length > 0 && (
                  <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2">
                    {selectedArticle.tags.map((tag) => (
                      <span key={tag} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Related Devices */}
                {selectedArticle.relatedDevices.length > 0 && (
                  <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2">
                    <span className="text-xs text-gray-500">关联设备：</span>
                    {selectedArticle.relatedDevices.map((dev) => (
                      <span key={dev} className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-mono whitespace-nowrap">
                        {dev}
                      </span>
                    ))}
                  </div>
                )}

                {/* Content */}
                <div className="px-6 py-6">
                  <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed">
                    {renderMarkdown(selectedArticle.content)}
                  </div>
                </div>

                {/* View Footer */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-teal-600 whitespace-nowrap">
                      <i className="ri-thumb-up-line"></i>
                      有帮助
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-teal-600 whitespace-nowrap">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedArticle(null);
                      setIsEditing(false);
                    }}
                    className="px-4 py-1.5 bg-teal-500 text-white text-xs font-medium rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                  >
                    关闭
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default KnowledgePage;